import { useState, useRef, useEffect } from "react";
import styled, { css } from "styled-components";
import { Menu, X, CalendarCheck } from "lucide-react";
import { usePage } from "@inertiajs/react";

import LanguageSwitch from "../_kit/LanguageSwitch";
import {
  propertyStories,
  propertyHome,
  propertyAbout,
  insightsHome,
  guestPortal,
} from "../../../components/insights/paths";
import { useLandingLanguage } from "../../../contexts/landing-language";
import {
  z,
  weight,
  shadow,
  sectionPad,
  radius,
  motion,
  media,
  font,
  container,
  color,
} from "../../../styles/tokens";

// Static ESM imports so Vite resolves ONE url for both the client and the SSR build. The
// previous require() + unmodule() produced a hashed production path server-side and a
// /vite-dev/ path client-side, so React reported a hydration mismatch on every render.
import logoLight from "../../../assets/logo-light.png";
import logoDark from "../../../assets/logo-dark.png";

/* A two-row masthead.
 *
 * Seven nav links, a language switcher and a Book Now button did not fit one row: the links were
 * absolutely centred and collided with the logo at intermediate widths. Splitting by PURPOSE rather
 * than by overflow — row one is where you can go and the action we want; row two is where you are
 * on this page — lets both rows breathe at a small, wide-tracked size and reads as deliberate
 * rather than crowded.
 */

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${z.header};
  color: ${(props) => (props.$scrolled ? color.ink : color.onDark)};
  background: ${(props) => (props.$scrolled ? color.canvas : "transparent")};
  box-shadow: ${(props) => (props.$scrolled ? shadow.sm : "none")};
  transition:
    background ${motion.base},
    color ${motion.base},
    box-shadow ${motion.base};
`;

/* Both rows share this, so the wordmark lines up with the section content below it. */
const Shell = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  ${media.sm`
    gap: 8px;
    padding-inline: 14px;
  `}
  width: 100%;
  max-width: ${container.default};
  margin: 0 auto;
  padding: 0 ${sectionPad.x};
`;

const PrimaryRow = styled.div`
  padding: 12px 0;
`;

const SectionRow = styled.div`
  padding: 7px 0;
  border-top: 1px solid
    ${(props) => (props.$scrolled ? "rgba(20, 21, 15, 0.14)" : "rgba(246, 244, 238, 0.22)")};
  transition: border-color ${motion.base};

  /* The drawer carries these on small screens. */
  ${media.lg`
    display: none;
  `}
`;

const Logo = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: auto;

  img {
    height: 34px;
    width: auto;
  }
`;

/* 11px at 0.12em, the size both rows use. Small enough that seven items never crowd, tracked
 * enough that it still reads as navigation rather than fine print. */
const linkType = css`
  font-family: ${font.body};
  font-size: 11px;
  font-weight: ${weight.medium};
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const PrimaryNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 28px;

  ${media.lg`
    display: none;
  `}
`;

const NavLink = styled.a`
  ${linkType}
  color: inherit;
  opacity: 0.85;
  transition: opacity ${motion.fast};

  &:hover {
    opacity: 1;
  }
`;

const PillLabel = styled.span`
  ${media.sm`
    /* Below 480px the row is logo + language + book + burger; the label is the first thing that
     * can go without losing a function. The accessible name stays on the link. */
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  `}
`;

const Pill = styled.a`
  ${linkType}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;

  ${media.sm`
    gap: 0;
    padding: 8px;
    border-radius: 999px;
  `}
  border: 1px solid currentColor;
  border-radius: ${radius.pill};
  color: inherit;
  transition:
    background ${motion.base},
    color ${motion.base};

  &:hover {
    background: ${(props) => (props.$scrolled ? color.ink : color.onDark)};
    color: ${(props) => (props.$scrolled ? color.onDark : color.ink)};
  }
`;

const SectionNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 26px;
`;

const SectionLabel = styled.p`
  ${linkType}
  margin: 0 4px 0 0;
  font-weight: ${weight.regular};
  opacity: 0.55;
`;

/* Pushed to the far edge, mirroring the primary row's action. */
const TrailingAction = styled.a`
  ${linkType}
  margin-left: auto;
  padding: 4px 13px;
  border: 1px solid currentColor;
  border-radius: ${radius.pill};
  color: inherit;
  opacity: 0.9;
  transition: opacity ${motion.fast};

  &:hover {
    opacity: 1;
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const TopLanguage = styled.div`
  display: flex;
`;

const MobileMenuButton = styled.button`
  display: none;
  padding: 4px;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;

  ${media.lg`
    display: flex;
  `}
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${z.overlay};
  background: rgba(20, 21, 15, 0.5);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
  transition: opacity ${motion.base};
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: ${z.modal};
  width: min(320px, 82vw);
  padding: 24px;
  background: ${color.canvas};
  color: ${color.ink};
  overflow-y: auto;
  transform: translateX(${(props) => (props.$isOpen ? "0" : "100%")});
  transition: transform ${motion.base};
`;

const CloseButton = styled.button`
  display: flex;
  margin: 0 0 24px auto;
  padding: 4px;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
`;

const MobileNavLinks = styled.nav`
  display: flex;
  flex-direction: column;
`;

const MobileNavLink = styled.a`
  ${linkType}
  padding: 16px 0;
  border-bottom: 1px solid ${color.border};
  color: ${color.ink};
`;

const MobileBookButton = styled.a`
  ${linkType}
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding: 14px;
  border: 1px solid ${color.primary};
  border-radius: ${radius.sm};
  background: ${color.primary};
  color: ${color.onDark};
`;

const Header = ({ forceScrolled = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);

  /* The drawer had none of this: no Escape, no focus trap, no scroll lock, and — because it is
   * always in the DOM at right: -100% — its links stayed in the tab order while closed, so a
   * keyboard user tabbed through an invisible menu on every page.
   */
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusable = drawerRef.current.querySelectorAll("a[href], button");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    drawerRef.current?.querySelector("button, a[href]")?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [mobileMenuOpen]);
  const [scrolledState, setScrolledState] = useState(false);
  const { t, language } = useLandingLanguage();
  const storiesEnabled = usePage().props.stories_enabled === true;
  const scrolled = forceScrolled || scrolledState;

  useEffect(() => {
    if (forceScrolled) return;
    const handleScroll = () => {
      setScrolledState(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [forceScrolled]);

  const home = propertyHome(language);
  const sectionHref = (id) => `${home}#${id}`;

  // Every nav item carries a real href, so it is a link to a crawler and works
  // with JavaScript off. When the section exists on the page we are already on,
  // intercept and scroll smoothly instead; otherwise let the browser follow the
  // href to /hebe#<id>, which is what the old router.visit branch did by hand.
  const scrollToSection = (event, id) => {
    const element = document.getElementById(id);
    if (!element) return;

    event.preventDefault();
    element.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  // Row one: where you can go on the site, plus the action the page exists for.
  // Row two: the sections of this page.
  const PRIMARY = [
    {
      key: "insights",
      href: insightsHome(language),
      label: t.nav.insights,
      external: true,
    },
    { key: "contact", href: sectionHref("contact"), label: t.nav.contact },
  ];
  // Row two carries the site's actual pages. It used to hold this page's section anchors, which
  // stopped meaning anything once about/facilities/rooms/gallery moved to their own page.
  const PAGES = [
    { key: "book", href: home, label: t.nav.book },
    { key: "about", href: propertyAbout(language), label: t.nav.about },
    // Only when the feature is on — the route 404s otherwise, so linking it would be a dead end.
    storiesEnabled
      ? {
          key: "stories",
          href: propertyStories(language),
          label: t.nav.stories,
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      <HeaderContainer $scrolled={scrolled}>
        <PrimaryRow>
          <Shell>
            <Logo href={home} onClick={(e) => scrollToSection(e, "home")}>
              <img
                src={scrolled ? logoDark : logoLight}
                alt="Hebe Hostel Bogor"
              />
            </Logo>

            <PrimaryNav aria-label={t.nav.home}>
              {PRIMARY.map((item) => (
                <NavLink
                  key={item.key}
                  href={item.href}
                  onClick={
                    item.external
                      ? undefined
                      : (e) => scrollToSection(e, item.key)
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </PrimaryNav>

            <RightActions>
              <TopLanguage>
                <LanguageSwitch tone={scrolled ? "light" : "dark"} />
              </TopLanguage>
              <Pill
                $scrolled={scrolled}
                href={sectionHref("booking")}
                onClick={(e) => scrollToSection(e, "booking")}
                aria-label={t.nav.bookNow}
              >
                <CalendarCheck size={16} aria-hidden="true" />
                <PillLabel>{t.nav.bookNow}</PillLabel>
              </Pill>
              <MobileMenuButton
                ref={menuButtonRef}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu size={22} />
              </MobileMenuButton>
            </RightActions>
          </Shell>
        </PrimaryRow>

        <SectionRow $scrolled={scrolled}>
          <Shell>
            <SectionNav aria-label={t.nav.sections}>
              <SectionLabel aria-hidden="true">{t.nav.sections}</SectionLabel>
              {PAGES.map((page) => (
                <NavLink key={page.key} href={page.href}>
                  {page.label}
                </NavLink>
              ))}
            </SectionNav>
            {/* A plain anchor, not an Inertia Link: it redirects into the HGS app, which lives
             * outside the bilingual marketing routes. */}
            <TrailingAction href={guestPortal()} rel="nofollow">
              {t.nav.guestPortal}
            </TrailingAction>
          </Shell>
        </SectionRow>
      </HeaderContainer>

      <MobileMenuOverlay
        $isOpen={mobileMenuOpen}
        onClick={() => setMobileMenuOpen(false)}
      />
      <MobileMenu
        id="mobile-menu"
        ref={drawerRef}
        $isOpen={mobileMenuOpen}
        inert={!mobileMenuOpen}
      >
        <CloseButton onClick={() => setMobileMenuOpen(false)}>
          <X size={24} />
        </CloseButton>
        <MobileNavLinks>
          {PAGES.map((page) => (
            <MobileNavLink key={page.key} href={page.href}>
              {page.label}
            </MobileNavLink>
          ))}
          {PRIMARY.map((item) => (
            <MobileNavLink key={item.key} href={item.href}>
              {item.label}
            </MobileNavLink>
          ))}
          <MobileNavLink href={guestPortal()} rel="nofollow">
            {t.nav.guestPortal}
          </MobileNavLink>
        </MobileNavLinks>
        <MobileBookButton
          href={sectionHref("booking")}
          onClick={(e) => scrollToSection(e, "booking")}
        >
          {t.nav.bookNow}
        </MobileBookButton>
      </MobileMenu>
    </>
  );
};

export default Header;
